<div class="pi-page">
  <div class="page-header">
    <div class="header-left">
      <h1>Promoting Interoperability</h1>
    </div>
    <div class="header-right">
      @if (relatedOrganizations().length > 0) {
        <div class="chip tin-rollup-chip" [matTooltip]="rollupTooltip" matTooltipPosition="below">
          TIN Roll Up <mat-icon class="rollup-info-icon">info</mat-icon>
        </div>
      } @else {
        <div class="chip">Practice: {{ practice }}</div>
      }
      <div class="chip">Performance: {{ performanceDuration }}</div>
      <div class="chip">TIN: {{ tin }}</div>
    </div>
  </div>

  @if (hasIncompleteMeasures) {
    <div class="incomplete-warning">
      <mat-icon>warning</mat-icon>
      <div class="warning-content">
        <strong>Warning:</strong> You have {{ incompleteMeasuresCount }}
        incomplete {{ incompleteMeasuresCount === 1 ? "measure" : "measures" }}
        selected. If any required measure is left blank, answered "No", or has a 0 numerator or denominator, the entire Promoting Interoperability category will score 0.
      </div>
    </div>
  }

  <div class="validation-cards">
    <div class="card" [class.valid]="cehrtValidated > 0">
      <div class="card-number">{{ cehrtValidated }}</div>
      <div class="card-content">
        <div class="card-title">CEHRT ID Validation</div>
        <div class="card-subtitle">(Required)</div>
      </div>
    </div>

    <div class="card" [class.valid]="allBlockingComplete">
      <mat-icon class="card-icon">{{
        allBlockingComplete ? "check" : "close"
      }}</mat-icon>
      <div class="card-content">
        <div class="card-title">Attestations</div>
        <div class="card-subtitle">(Required)</div>
        @if (!allBlockingComplete) {
          <button class="card-action-btn" (click)="openAttestationModal()">
            Complete Now
          </button>
        } @else {
          <button class="card-action-btn" (click)="openAttestationModal()">
            Edit
          </button>
        }
      </div>
    </div>

    <div class="card" [class.valid]="allRequiredComplete">
      <mat-icon class="card-icon">{{
        allRequiredComplete ? "check" : "close"
      }}</mat-icon>
      <div class="card-content">
        <div class="card-title">Required Measures</div>
        <div class="card-subtitle">
          (All Required Measures Must Be Complete)
        </div>
      </div>
    </div>

    <div class="points-card">
      <div class="points-section">
        <div class="points-label">
          Estimated PI Points
          <mat-icon
            *ngIf="isScoreCalculating()"
            class="score-calculating-icon"
            matTooltip="Saving — scores will update momentarily"
            matTooltipPosition="above"
          >sync</mat-icon>
        </div>
        <div class="points-calculation">
          <span class="points-value">{{ estimatedPoints }}</span>
          <span class="points-operator">+</span>
          <span class="bonus-value">{{ estimatedBonus }}</span>
          <mat-icon
            class="info-icon"
            matTooltip="Bonus points are capped at 5 total. Completing multiple optional Public Health measures will not result in more than 5 bonus points."
            matTooltipPosition="above"
            >info_outline</mat-icon
          >
          <span class="points-operator">=</span>
          <span class="total-value">{{ estimatedTotal }}/100</span>
        </div>
        <div class="points-sublabels">
          <span>Points</span>
          <span>Bonus</span>
          <span>Total</span>
        </div>
      </div>
      <div class="weight-section">
        <div class="weight-label">Estimated PI Weight</div>
        <div class="weight-value">
          {{ estimatedWeight | number: "1.1-1" }}/{{ reweighting().weights.pi }}
          @if (showReweightingIndicator) {
            <mat-icon
              class="reweighting-icon"
              [matTooltip]="reweightingTooltip"
              matTooltipPosition="above"
            >
              info
            </mat-icon>
          }
        </div>
        @if (!showReweightingIndicator) {
          <mat-icon
            class="info-icon"
            matTooltip="Weight calculated as (Total Points / 100) × 25"
            matTooltipPosition="above"
            >info_outline</mat-icon
          >
        }
      </div>
    </div>
  </div>

  <div *ngIf="!allBlockingComplete" class="disclaimer blocking-warning">
    <mat-icon>warning</mat-icon>
    <div>
      <strong>Action Required:</strong> You must complete the required
      attestations before you can proceed to the next step.
      <button
        mat-stroked-button
        color="warn"
        (click)="openAttestationModal()"
        style="margin-left: 12px"
      >
        Complete Attestations
      </button>
    </div>
  </div>

  <div class="date-and-upload-row">
    <div class="date-picker-minimal">
      <mat-form-field appearance="outline">
        <mat-label>Performance Start Date</mat-label>
        <input
          matInput
          [matDatepicker]="startPicker"
          [(ngModel)]="performanceStartDate"
          [min]="minDate"
          [max]="maxDate"
          [matDatepickerFilter]="dateFilter"
          [errorStateMatcher]="startDateErrorMatcher"
          (dateChange)="onPerformanceStartDateChange($event.value)"
          required
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="startPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
        <mat-error *ngIf="performanceStartDateInvalid">
          Performance start date must be within 2025
        </mat-error>
        <mat-error *ngIf="startDateBeforeEndDateError">
          Start date must be before end date
        </mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Performance End Date</mat-label>
        <input
          matInput
          [matDatepicker]="endPicker"
          [(ngModel)]="performanceEndDate"
          [min]="minDate"
          [max]="maxDate"
          [matDatepickerFilter]="dateFilter"
          [errorStateMatcher]="endDateErrorMatcher"
          (dateChange)="onPerformanceEndDateChange($event.value)"
          required
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="endPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
        <mat-error *ngIf="performanceEndDateInvalid">
          Performance end date must be within 2025
        </mat-error>
      </mat-form-field>

      <div
        *ngIf="dateRangeInvalid && startDateBeforeEndDateError"
        class="date-warning-minimal date-range-error"
      >
        <mat-icon class="warning-icon">warning</mat-icon>
        Start date must be before end date
      </div>

      <div
        *ngIf="!performanceStartDate || !performanceEndDate"
        class="date-warning-minimal"
      >
        Performance period required
      </div>
    </div>

    <div class="file-upload-minimal">
      <div class="upload-header-minimal read-only-allowed" (click)="toggleFileUploadSection()">
        <span class="upload-title"
          >Supporting Documentation ({{ piDocuments.length }}/10)</span
        >
        <div class="header-actions">
          <button
            class="btn-upload-minimal"
            (click)="fileInput.click(); $event.stopPropagation()"
            [disabled]="!canUploadMoreDocuments"
          >
            <mat-icon>add</mat-icon>
            Upload
          </button>
          <mat-icon class="expand-icon">{{
            fileUploadExpanded ? "expand_less" : "expand_more"
          }}</mat-icon>
        </div>
      </div>

      <input
        type="file"
        #fileInput
        (change)="onFileUpload($event)"
        accept=".pdf,.jpg,.jpeg,.png"
        hidden
      />

      <div
        *ngIf="fileUploadExpanded && piDocuments.length > 0"
        class="documents-list-minimal"
      >
        <div
          *ngFor="let doc of piDocuments"
          class="document-item-minimal"
          [matTooltip]="doc.fileName"
          matTooltipPosition="above"
        >
          <mat-icon class="file-icon-minimal">description</mat-icon>
          <span class="file-name-minimal">{{ doc.fileName }}</span>
          <div class="file-actions-minimal">
            <button mat-icon-button (click)="viewFile(doc)" matTooltip="View" class="read-only-allowed">
              <mat-icon>visibility</mat-icon>
            </button>
            <button
              mat-icon-button
              (click)="downloadFile(doc)"
              matTooltip="Download"
              class="read-only-allowed"
            >
              <mat-icon>download</mat-icon>
            </button>
            <button
              mat-icon-button
              (click)="removeUploadedFile(doc.documentId)"
              matTooltip="Delete"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="table-header">
    <div class="th">Measure ID</div>
    <div class="th">Measure Title</div>
    <div class="th">Exclusions</div>
    <div class="th">Response</div>
    <div class="th">Performance</div>
    <div class="th">Points</div>
  </div>

  <div *ngFor="let category of categories" class="category-section">
    <div class="category-header-bar read-only-allowed" (click)="toggleCategory(category)">
      <span class="category-name">{{ category.displayName }}</span>
      <mat-icon class="expand-icon">{{
        category.isExpanded ? "expand_less" : "expand_more"
      }}</mat-icon>
    </div>

    <div
      *ngIf="category.category === 'e_prescribing' && hasPdmpExclusionError"
      class="validation-error"
    >
      <mat-icon class="error-icon">error</mat-icon>
      <span>{{ pdmpExclusionErrorMessage }}</span>
    </div>

    <div *ngIf="category.isExpanded">
      <div *ngFor="let measure of category.measures" class="measure-row">
        <div class="measure-main-row">
          <div class="td td-id">
            <mat-checkbox
              class="measure-checkbox"
              [(ngModel)]="measure.selected"
              (ngModelChange)="onMeasureCheckboxChange(measure)"
              [disabled]="isHieMeasureDisabled(measure)"
            ></mat-checkbox>
            <span class="measure-id-text">
              {{ measure.measureId }}
              <span
                *ngIf="measure.isRequired && !measure.isBlocking"
                class="required-badge"
                >REQ</span
              >
              <span
                *ngIf="
                  measure.measureId === 'PI_HIE_5' ||
                  measure.measureId === 'PI_HIE_6'
                "
                class="alt-badge"
                matTooltip="Alternative: Select PI_HIE_1 & PI_HIE_4 together, OR select PI_HIE_5, OR select PI_HIE_6"
                >ALT</span
              >
              <mat-icon
                *ngIf="isSuspendedMeasure(measure.measureId)"
                class="suspended-icon"
                [matTooltip]="getSuspendedMeasureTooltip(measure.measureId)"
                matTooltipPosition="right"
              >
                info
              </mat-icon>
            </span>
          </div>

          <div class="td td-title">
            <span>{{ measure.measureTitle }}</span>
            <button
              *ngIf="measure.pdfUrl"
              mat-icon-button
              class="pdf-link read-only-allowed"
              (click)="openPdf(measure)"
            >
              <mat-icon>picture_as_pdf</mat-icon>
            </button>
          </div>

          <div class="td">
            <div
              *ngIf="measure.exclusionClaimed"
              class="exclusion-claimed-container"
            >
              <span class="exclusion-claimed-text">Exclusion Claimed</span>
              <button
                class="btn-clear-exclusion"
                (click)="clearExclusion(measure)"
                type="button"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <button
              *ngIf="
                !measure.exclusionClaimed &&
                measure.exclusions &&
                measure.exclusions.length > 0 &&
                measure.selected &&
                (measure.valueType !== 'yes_no_engagement' ||
                  measure.attestation === false)
              "
              class="btn-claim-exclusion"
              (click)="openExclusionModal(measure)"
              type="button"
            >
              Claim Exclusion
            </button>
          </div>

          <div class="td td-response">
            <div *ngIf="!measure.exclusionClaimed">
              <div
                *ngIf="
                  measure.valueType === 'yes_no_engagement' && measure.selected
                "
                class="engagement-container"
              >
                <mat-radio-group
                  [(ngModel)]="measure.attestation"
                  (ngModelChange)="onAttestationChange(measure, $event)"
                  [disabled]="!measure.selected"
                >
                  <mat-radio-button [value]="true">Yes</mat-radio-button>
                  <mat-radio-button [value]="false">No</mat-radio-button>
                </mat-radio-group>

                <mat-form-field
                  *ngIf="measure.attestation === true"
                  appearance="outline"
                  class="engagement-level-select"
                >
                  <mat-label>Engagement Level</mat-label>
                  <mat-select
                    placeholder="Select Level"
                    [(ngModel)]="measure.activeEngagementLevel"
                    (ngModelChange)="onEngagementLevelChange(measure, $event)"
                    [disabled]="!measure.selected"
                  >
                    <mat-option value="preProduction"
                      >Pre-Production</mat-option
                    >
                    <mat-option value="validatedProduction"
                      >Validated Production</mat-option
                    >
                  </mat-select>
                </mat-form-field>
              </div>

              <div
                *ngIf="
                  measure.valueType === 'numerator_denominator' &&
                  measure.selected
                "
                class="num-den-inputs"
              >
                <div class="num-den-field">
                  <label class="num-den-label">Numerator</label>
                  <input
                    type="number"
                    class="num-input"
                    [class.error-input]="hasMeasureError(measure.measureId)"
                    [(ngModel)]="measure.numerator"
                    (ngModelChange)="onNumeratorChange(measure, $event)"
                    [disabled]="!measure.selected"
                    min="0"
                  />
                </div>
                <div class="num-den-field">
                  <label class="num-den-label">Denominator</label>
                  <input
                    type="number"
                    class="num-input"
                    [class.error-input]="hasMeasureError(measure.measureId)"
                    [(ngModel)]="measure.denominator"
                    (ngModelChange)="onDenominatorChange(measure, $event)"
                    [disabled]="!measure.selected"
                    min="0"
                  />
                </div>
                <div
                  *ngIf="hasMeasureError(measure.measureId)"
                  class="validation-error"
                >
                  <mat-icon class="error-icon">error</mat-icon>
                  <span>{{ getMeasureError(measure.measureId) }}</span>
                </div>
              </div>

              <div *ngIf="measure.valueType === 'yes_no' && measure.selected">
                <mat-radio-group
                  [(ngModel)]="measure.attestation"
                  (ngModelChange)="onAttestationChange(measure, $event)"
                  [disabled]="!measure.selected"
                >
                  <mat-radio-button [value]="true">Yes</mat-radio-button>
                  <mat-radio-button [value]="false">No</mat-radio-button>
                </mat-radio-group>
              </div>

              <div *ngIf="!measure.selected" class="selection-hint">
                Select measure to enter data
              </div>
            </div>

            <div
              *ngIf="measure.exclusionClaimed"
              class="exclusion-claimed-message"
            >
              Exclusion claimed - no data entry required
            </div>
          </div>

          <div class="td">
            <div
              *ngIf="
                measure.valueType === 'numerator_denominator' &&
                (measure.performance ?? 0) > 0 &&
                !hasMeasureError(measure.measureId)
              "
              class="performance-circle"
            >
              {{ measure.performance ?? 0 | number: "1.1-1" }}
            </div>
            <div
              *ngIf="
                measure.valueType === 'numerator_denominator' &&
                hasMeasureError(measure.measureId)
              "
              class="performance-circle error"
            >
              —
            </div>
          </div>

          <div class="td td-points">
            <mat-icon
              *ngIf="isScoreCalculating()"
              class="measure-calculating-icon"
              matTooltip="Saving — scores will update momentarily"
              matTooltipPosition="left"
            >sync</mat-icon>

            <ng-container *ngIf="!isScoreCalculating()">
              <span *ngIf="measure.isBonusMeasure" class="points-display">
                <div class="points-display">
                  {{ getMeasurePointsDisplay(measure) }}
                </div>
                <span
                  *ngIf="
                    shouldShowBonusPoints(measure.measureId) && measure.completed
                  "
                  class="bonus-badge"
                  matTooltip="First selected bonus measure receives 5 points"
                >
                  +5
                </span>
              </span>

              <span
                *ngIf="
                  !measure.isBonusMeasure &&
                  !measure.isBlocking &&
                  (measure.maxPoints ?? 0) > 0
                "
                class="points-display"
              >
                {{ measure.points }}/{{ getAdjustedMaxPoints(measure) }}
                <span
                  *ngIf="hasRedistributedPoints(measure)"
                  class="bonus-badge"
                  [matTooltip]="getRedistributionTooltip(measure)"
                  matTooltipPosition="left"
                >
                  +{{ getRedistributedPointsAmount(measure) }}
                </span>
              </span>

              <span
                *ngIf="
                  !measure.isBonusMeasure &&
                  !measure.isBlocking &&
                  (measure.maxPoints ?? 0) === 0
                "
                class="no-points"
              >
                N/A
              </span>
            </ng-container>
          </div>
        </div>

        <div
          *ngIf="measure.exclusionClaimed"
          class="measure-detail-row exclusion-details"
        >
          <div class="exclusion-content">
            <div class="exclusion-label">{{ measure.measureId }}</div>
            <div class="exclusion-title">{{ measure.measureTitle }}</div>
            <div class="exclusion-actions">
              <button
                class="btn-change-exclusion"
                (click)="openExclusionModal(measure)"
                type="button"
              >
                Change Exclusion Reason
              </button>
              <span class="exclusion-status">✓ Exclusion Claimed</span>
            </div>
          </div>
        </div>

        <div class="measure-status-row">
          <mat-icon class="status-icon">{{
            measure.completed ? "check_circle" : "radio_button_unchecked"
          }}</mat-icon>
          <span>{{ measure.completed ? "Complete" : "Incomplete" }}</span>
        </div>
      </div>
    </div>
  </div>
</div>
